import { HttpClient } from "@angular/common/http";
import { Injectable, type OnDestroy, inject } from "@angular/core";
import { type Observable, Subject } from "rxjs";
import { map, tap } from "rxjs/operators";
import { environment } from "../../../../environments/environment";
import type { BulkUserAppNotificationUpdateModel } from "../../../shared/model/bulk-user-app-notification-update.model";
import { createRequestOption } from "../../../shared/util/request-util";
import type { IUserAppNotification } from "../model/user-app-notification.model";

interface GetAllResponseModel {
    notifications: IUserAppNotification[];
    totalCount: number;
}

export interface GetAllRequestModel {
    dateStart?: string;
    dateEnd?: string;
    search?: string;
    clientName?: string;
    clientStatus?: string;
    locationIds?: number[];
    managerId?: number;
    bookkeeperId?: number;
    partnerInChargeId?: number;
    customFields?: string;
    readFlag?: boolean;
    inactive?: boolean;
    page: number;
    size: number;
    sort: string[];
}

@Injectable({ providedIn: "root" })
export class UserAppNotificationService implements OnDestroy {
    private readonly http = inject(HttpClient);

    private readonly resourceUrl: string =
        environment.apiUrl + "api/user-app-notification";

    private readonly update$: Subject<void> = new Subject<void>();

    ngOnDestroy(): void {
        this.update$.complete();
    }

    get onUpdate(): Observable<void> {
        return this.update$.asObservable();
    }

    notifyChanged(): void {
        this.update$.next();
    }

    update(
        userAppNotificationDTO: IUserAppNotification,
    ): Observable<IUserAppNotification> {
        return this.http
            .put<IUserAppNotification>(this.resourceUrl, userAppNotificationDTO)
            .pipe(tap(() => this.update$.next()));
    }

    bulkUpdate(
        req: BulkUserAppNotificationUpdateModel,
    ): Observable<IUserAppNotification[]> {
        return this.http
            .post<
                IUserAppNotification[]
            >(`${this.resourceUrl}/bulk-update`, req)
            .pipe(tap(() => this.update$.next()));
    }

    getAll(requestModel: GetAllRequestModel): Observable<GetAllResponseModel> {
        const params = createRequestOption(requestModel);
        return this.http
            .get<IUserAppNotification[]>(this.resourceUrl, {
                params: params,
                observe: "response",
            })
            .pipe(
                map((res) => ({
                    notifications: res.body,
                    totalCount: Number.parseInt(
                        res.headers.get("X-Total-Count") ?? "",
                        10,
                    ),
                })),
            );
    }
}
